import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getBackendUrl } from '../lib/apiUrls';
import { createWorkspaceClient } from '../lib/workspaceClient';

function normalizeList(list) {
  return {
    ...list,
    id: list?.id || list?.listId || list?._id || '',
    scope: list?.scope || (list?.scopeType === 'family' ? 'family' : 'personal'),
  };
}

function normalizeListItem(item) {
  return {
    ...item,
    id: item?.id || item?.itemId || item?._id || '',
  };
}

export function useLists(currentUser) {
  const workspaceClient = useMemo(() => createWorkspaceClient(getBackendUrl()), []);
  const [lists, setLists] = useState([]);
  const [activeListId, setActiveListId] = useState('');
  const [activeListItems, setActiveListItems] = useState([]);
  const [listStatusFilter, setListStatusFilter] = useState('all');
  const [listError, setListError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedLists, setHasLoadedLists] = useState(false);
  const listsRef = useRef(lists);
  const listStatusFilterRef = useRef(listStatusFilter);

  useEffect(() => {
    listsRef.current = lists;
  }, [lists]);

  useEffect(() => {
    listStatusFilterRef.current = listStatusFilter;
  }, [listStatusFilter]);

  const selectedList = useMemo(
    () => lists.find((list) => list.id === activeListId) || null,
    [lists, activeListId]
  );

  const resolveListScope = useCallback(
    (listId, fallback = 'personal') =>
      listsRef.current.find((list) => list.id === listId)?.scope || fallback,
    []
  );

  const loadLists = useCallback(async () => {
    if (!currentUser?.userId) {
      setLists([]);
      setActiveListId('');
      setActiveListItems([]);
      setHasLoadedLists(true);
      return [];
    }

    setIsLoading(true);
    setListError(null);

    try {
      const response = await workspaceClient.getLists(currentUser.userId);
      const fetchedLists = Array.isArray(response?.lists) ? response.lists : response;
      const nextLists = (Array.isArray(fetchedLists) ? fetchedLists : []).map(normalizeList);
      setLists(nextLists);
      return nextLists;
    } catch (error) {
      setListError(error?.error || error?.message || 'Could not load lists.');
      return [];
    } finally {
      setHasLoadedLists(true);
      setIsLoading(false);
    }
  }, [currentUser?.userId, workspaceClient]);

  const loadListItems = useCallback(
    async (listId, status = listStatusFilterRef.current, scopeOverride) => {
      if (!currentUser?.userId || !listId) {
        setActiveListItems([]);
        return { list: null, items: [] };
      }

      const resolvedStatus = status || listStatusFilterRef.current || 'all';
      setIsLoading(true);
      setListError(null);
      setListStatusFilter(resolvedStatus);

      try {
        const scope = scopeOverride || resolveListScope(listId);
        const response = await workspaceClient.getListItems(currentUser.userId, listId, {
          status: resolvedStatus,
          scope,
        });
        const nextList = response?.list ? normalizeList(response.list) : null;
        const nextItems = (Array.isArray(response?.items) ? response.items : []).map(
          normalizeListItem
        );

        if (nextList) {
          setLists((current) => {
            const existing = current.some((list) => list.id === nextList.id);
            if (!existing) {
              return [...current, nextList];
            }

            return current.map((list) => (list.id === nextList.id ? { ...list, ...nextList } : list));
          });
        }

        setActiveListId(listId);
        setActiveListItems(nextItems);

        return { list: nextList, items: nextItems };
      } catch (error) {
        setListError(error?.error || error?.message || 'Could not load list items.');
        setActiveListItems([]);
        return { list: null, items: [] };
      } finally {
        setIsLoading(false);
      }
    },
    [currentUser?.userId, resolveListScope, workspaceClient]
  );

  useEffect(() => {
    void loadLists();
  }, [loadLists]);

  const refreshLists = useCallback(async () => {
    const nextLists = await loadLists();

    if (activeListId) {
      const stillExists = nextLists.some((list) => list.id === activeListId);
      if (stillExists) {
        await loadListItems(activeListId, listStatusFilterRef.current);
      } else {
        setActiveListId('');
        setActiveListItems([]);
      }
    }
  }, [activeListId, loadListItems, loadLists]);

  const openList = useCallback(
    async (listId, status = listStatusFilterRef.current, scope) => {
      await loadListItems(listId, status, scope);
    },
    [loadListItems]
  );

  const updateList = useCallback(
    async (updates) => {
      if (!currentUser?.userId || !selectedList?.id) {
        return null;
      }

      setIsLoading(true);
      setListError(null);

      try {
        const response = await workspaceClient.updateList(currentUser.userId, selectedList.id, updates, {
          scope: selectedList.scope,
        });
        const updatedList = normalizeList(response?.list || response?.data?.list || response || {});
        await loadLists();
        await loadListItems(
          updatedList.id || selectedList.id,
          listStatusFilterRef.current,
          selectedList.scope
        );
        return updatedList;
      } catch (error) {
        setListError(error?.error || error?.message || 'Could not update list.');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [
      currentUser?.userId,
      loadListItems,
      loadLists,
      selectedList?.id,
      selectedList?.scope,
      workspaceClient,
    ]
  );

  const createList = useCallback(
    async (newList) => {
      if (!currentUser?.userId) {
        return null;
      }

      setIsLoading(true);
      setListError(null);

      try {
        const response = await workspaceClient.createList(currentUser.userId, newList);
        const createdList = normalizeList(response?.list || response?.data?.list || response || {});
        await loadLists();
        return createdList.id ? createdList : null;
      } catch (error) {
        setListError(error?.error || error?.message || 'Could not create list.');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [currentUser?.userId, loadLists, workspaceClient]
  );

  const deleteList = useCallback(async () => {
    if (!currentUser?.userId || !selectedList?.id) {
      return false;
    }

    setIsLoading(true);
    setListError(null);

    try {
      await workspaceClient.deleteList(currentUser.userId, selectedList.id, {
        scope: selectedList.scope,
      });
      await loadLists();
      setActiveListId('');
      setActiveListItems([]);
      return true;
    } catch (error) {
      setListError(error?.error || error?.message || 'Could not delete list.');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [currentUser?.userId, loadLists, selectedList?.id, selectedList?.scope, workspaceClient]);

  const createListItem = useCallback(
    async (item) => {
      if (!currentUser?.userId || !activeListId) {
        return null;
      }

      setIsLoading(true);
      setListError(null);

      try {
        const response = await workspaceClient.createListItem(currentUser.userId, activeListId, item, {
          scope: selectedList?.scope,
        });
        const responseItems = Array.isArray(response?.items) ? response.items : [];
        const createdItem = normalizeListItem(
          response?.item || responseItems[0] || response?.data?.item || response || {}
        );
        await loadLists();
        await loadListItems(activeListId, listStatusFilterRef.current, selectedList?.scope);
        return createdItem.id ? createdItem : null;
      } catch (error) {
        setListError(error?.error || error?.message || 'Could not create list item.');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [
      activeListId,
      currentUser?.userId,
      loadListItems,
      loadLists,
      selectedList?.scope,
      workspaceClient,
    ]
  );

  const updateListItem = useCallback(
    async (itemId, updates) => {
      if (!currentUser?.userId || !activeListId || !itemId) {
        return null;
      }

      setIsLoading(true);
      setListError(null);

      try {
        const response = await workspaceClient.updateListItem(
          currentUser.userId,
          activeListId,
          itemId,
          updates,
          { scope: selectedList?.scope }
        );
        const updatedItem = normalizeListItem(response?.item || response?.data?.item || response || {});
        await loadListItems(activeListId, listStatusFilterRef.current, selectedList?.scope);
        return updatedItem.id ? updatedItem : null;
      } catch (error) {
        setListError(error?.error || error?.message || 'Could not update list item.');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [
      activeListId,
      currentUser?.userId,
      loadListItems,
      selectedList?.scope,
      workspaceClient,
    ]
  );

  const deleteListItem = useCallback(
    async (itemId) => {
      if (!currentUser?.userId || !activeListId || !itemId) {
        return false;
      }

      setIsLoading(true);
      setListError(null);

      try {
        await workspaceClient.deleteListItem(currentUser.userId, activeListId, itemId, {
          scope: selectedList?.scope,
        });
        await loadLists();
        await loadListItems(activeListId, listStatusFilterRef.current, selectedList?.scope);
        return true;
      } catch (error) {
        setListError(error?.error || error?.message || 'Could not delete list item.');
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [
      activeListId,
      currentUser?.userId,
      loadListItems,
      loadLists,
      selectedList?.scope,
      workspaceClient,
    ]
  );

  const canShowEmptyState = useMemo(
    () => !isLoading && !lists.length && !listError,
    [isLoading, listError, lists.length]
  );

  return {
    lists,
    selectedList,
    activeListId,
    activeListItems,
    listStatusFilter,
    listError,
    isLoading,
    canShowEmptyState,
    openList,
    refreshLists,
    setListStatusFilter,
    createList,
    updateList,
    deleteList,
    createListItem,
    updateListItem,
    deleteListItem,
    setActiveListId,
    setActiveListItems,
    hasLoadedLists,
    hasLists: lists.length > 0,
  };
}
